import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { type ReactElement, type ReactNode, useId } from 'react';

import { hasValue } from '@core/utils/equality';
import { DEFAULT_ALL_VALUE_FOR_SELECT } from '@web/constants/app';
import { getMaxVisibleItems } from '@web/helpers/app';

import { InputTooltip } from '../InputTooltip/InputTooltip';

export type SelectorOption = {
  name: string;
  id: string;
  subLabel?: string;
};

type SelectorProps = {
  label: string;
  value: string[];
  size?: 'small' | 'medium';
  onChange: (selectedOptions: string[]) => void;
  options: SelectorOption[];
  /** @default '15rem' */
  selectMinWidth?: string;
  /** @default '25rem' */
  selectMaxWidth?: string;
  multiple?: boolean;
  hasSelectAll?: boolean;
  withCheckbox?: boolean;
  renderValue?: (selected: string[]) => ReactNode;
  maxVisibleValueItems?: number;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  tip?: string;
};

/**
 * Construct a simple selector
 */
export const Selector = ({
  disabled,
  label,
  value,
  onChange,
  options,
  size = 'medium',
  selectMinWidth = '15rem',
  selectMaxWidth = '25rem',
  multiple,
  hasSelectAll,
  withCheckbox,
  renderValue,
  maxVisibleValueItems = 1,
  sx,
  tip,
}: SelectorProps): ReactElement => {
  const theme = useTheme();
  const labelId = useId();

  const isSelectedAll = value.length === options.length;

  const defaultRenderValue = (): ReactNode => {
    const labels = options
      .filter((opt) => opt.id !== null && value.includes(opt.id))
      .map((opt) => opt.name);
    const { displayItems, extraCount } = getMaxVisibleItems(labels, maxVisibleValueItems);

    return `${displayItems.join(', ')}${extraCount > 0 ? ` +${extraCount}` : ''}`;
  };

  return (
    <FormControl
      sx={{
        minWidth: selectMinWidth,
        maxWidth: selectMaxWidth,
        [theme.breakpoints.down('lg')]: {
          maxWidth: '100%',
          flex: 1,
        },
      }}
    >
      <InputLabel id={labelId}>
        <Box display='flex' gap='0.25rem' alignItems='center'>
          {label}
          {tip !== undefined && <InputTooltip tip={tip} />}
        </Box>
      </InputLabel>
      <Select
        labelId={labelId}
        disabled={disabled}
        sx={{
          backgroundColor: theme.palette.SlabGray[0],
          ...sx,
        }}
        renderValue={renderValue !== undefined ? renderValue : defaultRenderValue}
        size={size}
        value={value}
        onChange={(e): void => {
          const newValue = e.target.value;

          if (hasSelectAll === true && newValue.includes(DEFAULT_ALL_VALUE_FOR_SELECT)) {
            onChange(isSelectedAll ? [] : options.map((opt) => opt.id));
            return;
          }
          if (typeof newValue === 'string') {
            onChange([newValue]);
            return;
          }
          onChange(newValue.filter(hasValue));
        }}
        label={label}
        multiple={multiple}
      >
        {hasSelectAll === true && options.length > 1 && (
          <MenuItem
            value={DEFAULT_ALL_VALUE_FOR_SELECT}
            className={isSelectedAll ? 'Mui-selected' : ''}
          >
            {withCheckbox === true && <Checkbox checked={isSelectedAll} />}
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Typography
                variant='body2'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                All
              </Typography>
            </Box>
          </MenuItem>
        )}
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {withCheckbox === true && <Checkbox checked={value.includes(opt.id)} />}
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Typography
                variant='body2'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {opt.name}
                {opt.subLabel !== undefined && (
                  <Typography
                    className='subLabel'
                    variant='body3'
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {opt.subLabel}
                  </Typography>
                )}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
